import { Children, isValidElement, type ReactNode } from 'react'

/**
 * Marker class the final reveal targets (final-reveal.tsx queries it, the
 * imperative pass adds `chat-reveal-unit` + a delay to each one). The spans
 * are inert on their own — no styles, no animation — so a render that never
 * runs a reveal pass shows them as plain text.
 */
export const REVEAL_CHUNK_CLASS = 'chat-reveal-chunk'

/** Inline tags safe to wrap whole in a chunk span. Anything else (nested
 * lists, hard breaks) is left unwrapped and reveals with its own chunks or
 * its containing block. */
const INLINE_TAGS = new Set(['a', 'strong', 'em', 'b', 'i', 'code', 'span', 'del', 'sup', 'sub', 'img'])

function chunkString(text: string, out: Array<ReactNode>, key: () => number) {
  // Split keeping whitespace so joined chunks reproduce the text exactly.
  // One word per chunk, its surrounding whitespace riding along inside the span.
  const tokens = text.split(/(\s+)/).filter(Boolean)
  let buf: Array<string> = []
  let bufHasWord = false
  const flush = () => {
    if (!buf.length) return
    out.push(
      <span key={key()} className={REVEAL_CHUNK_CLASS}>
        {buf.join('')}
      </span>,
    )
    buf = []
    bufHasWord = false
  }
  for (const tok of tokens) {
    // Whitespace rides with the word before it (never a chunk of its own),
    // so a new word closes the previous chunk.
    if (!/^\s+$/.test(tok) && bufHasWord) flush()
    buf.push(tok)
    bufHasWord = bufHasWord || !/^\s+$/.test(tok)
  }
  flush()
}

/**
 * Wrap a block's inline children into reveal-chunk spans: plain text splits
 * into per-word chunks, simple inline elements (links, bold, inline code…)
 * become one chunk each. Purely structural — React owns the spans, so the
 * reveal pass can restyle them without fighting reconciliation when the text
 * grows mid-reveal.
 */
export function chunkifyForReveal(children: ReactNode): ReactNode {
  const out: Array<ReactNode> = []
  let nextKey = 0
  const key = () => nextKey++

  for (const child of Children.toArray(children)) {
    if (typeof child === 'string') {
      chunkString(child, out, key)
    } else if (typeof child === 'number') {
      chunkString(String(child), out, key)
    } else if (isValidElement(child) && typeof child.type === 'string' && INLINE_TAGS.has(child.type)) {
      out.push(
        <span key={key()} className={REVEAL_CHUNK_CLASS}>
          {child}
        </span>,
      )
    } else {
      out.push(child)
    }
  }
  return out
}
